import { useCallback, useEffect, useRef, useState } from 'react'
import { useChannelActionContext, useChannelStateContext } from 'stream-chat-react'
import { createStyles } from '@codeleap/styles'
import { Icon } from '@/components'
import { appStatusStore } from '@/stores'

const DISTANCE_FROM_BOTTOM_THRESHOLD = 100

// @spec FR-0077/AC-6@v1 — floating scroll-to-latest button (paridade mobile).
export const ChatScrollToBottomButton = () => {
  const { hasMoreNewer } = useChannelStateContext()
  const { jumpToLatestMessage } = useChannelActionContext()
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true)
  const listElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    // Stream's MessageList re-mounts on channel switch, so the `.str-chat__list`
    // node lookup has to happen post-mount — there is no exposed ref. A short
    // poll handles the few frames between channel render and list element
    // attachment without a flicker on the button visibility.
    let polledRaf = 0
    let attachedList: HTMLElement | null = null
    let attachedHandler: (() => void) | null = null
    const tryAttach = () => {
      const list = document.querySelector<HTMLElement>('.str-chat__list')
      if (!list) {
        polledRaf = requestAnimationFrame(tryAttach)
        return
      }
      listElementRef.current = list
      const checkScroll = () => {
        const distance = list.scrollHeight - list.scrollTop - list.clientHeight
        setIsScrolledToBottom(distance < DISTANCE_FROM_BOTTOM_THRESHOLD)
      }
      list.addEventListener('scroll', checkScroll, { passive: true })
      checkScroll()
      attachedList = list
      attachedHandler = checkScroll
    }
    polledRaf = requestAnimationFrame(tryAttach)
    return () => {
      cancelAnimationFrame(polledRaf)
      if (attachedList && attachedHandler) {
        attachedList.removeEventListener('scroll', attachedHandler)
      }
    }
  }, [])

  const handleClick = useCallback(async () => {
    if (hasMoreNewer) {
      appStatusStore.set('loading')
      // Two paint frames between `set('loading')` and the actual reload so
      // the overlay is on screen before Stream's channel re-render takes
      // over the main thread (~1.5s rendering 50+ messages synchronously).
      // Without this gap the browser never paints the loading state.
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))
      try {
        await jumpToLatestMessage()
      } finally {
        // And again on the trailing edge — let the post-reload paint land
        // before we drop the overlay, otherwise the user catches a frame of
        // the fresh channel with the spinner still on top.
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))
        appStatusStore.set('idle')
      }
      return
    }
    const list = listElementRef.current
    if (!list) return
    list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' })
  }, [hasMoreNewer, jumpToLatestMessage])

  if (isScrolledToBottom && !hasMoreNewer) return null

  return (
    <button
      type='button'
      css={styles.button}
      onClick={handleClick}
      aria-label='Scroll to latest message'
    >
      <Icon name='chevron-down' style={styles.icon} />
    </button>
  )
}

const styles = createStyles(theme => ({
  button: {
    position: 'absolute',
    bottom: theme.spacing.value(1),
    right: theme.spacing.value(3),
    width: 44,
    height: 44,
    borderRadius: 22,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: theme.colors.neutralSolid0,
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
    transition: 'background-color 120ms ease',
    '&:hover': {
      backgroundColor: theme.colors.neutralSolid100,
    },
  },
  icon: {
    width: 20,
    height: 20,
    color: theme.colors.textBody,
  },
}))
